import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import {
  AlertDialogSlide,
  ButtonWithTooltip,
  MoreButton,
  SideDrawer,
  toast,
} from '@sales-center/components'
import { useNavigate } from 'react-router-dom'

import {
  ENTITY_TYPES,
  SOLD_SERVICE_STATUS,
  updateLoadingState,
} from '@sales-center/common'
import { useDialogState } from '@user/utils/useDialogState'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  clearOpportunityDetail,
  deleteOpportunity,
  getOpportunities,
  getOpportunityDetail,
  IOpportunity,
  updateReassignOpportunity,
} from '@user/store/opportunity'
import { useSelectOppStages } from '@user/store/salesFunnel'
import { permissionDenied } from '@user/store/authorization/permissions'

import { useUpdateOppStatus } from '../hooks'
import { AddNoteDrawer } from '../../Notes/AddNoteDrawer'
import { OPPORTUNITY_STATUSES, OppStatus } from '../OpportunityCard/typings'
import { ReAssignSalesTeamForm } from '../../SalesTeam/ReAssignSalesTeam/ReAssignSalesTeamForm'
import { AttachmentsDrawer } from '@user/components/Attachments/AddAttachmentsDrawer'
import { useReopenStatus } from './useReopenStatus'

import Box from '@mui/material/Box'
import {
  ReOpenPopupVariant,
  soldServiceStatusToReOpenPopupVariant,
} from '../ReOpenOppPopup/typings'
import { OpportunityStatusSelect } from '@user/components/Opportunities/OpportunityCard/OpportunityStatusSelect/OpportunityStatusSelect'
import Stack from '@mui/material/Stack'
import { ViewNotesModal } from '@user/components/Notes/ViewNotesModal/ViewNotesModal'
import { EditNoteDrawer } from '@user/components/Notes/EditNoteDrawer/EditNoteDrawer'
import { NotesButton } from '@user/components/Leads/LeadCard/NotesDrawer'
import { useLoadEntityLocationsOnMount } from '@user/store/entityLocations'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { ReOpenOppPopupNew } from '@user/components/Opportunities/ReOpenOppPopup/ReOpenOppPopupNew'
import { getSalesTeam, getSalesTeamUsers } from '@user/store/salesTeam'

const getStyles = (theme: Theme) => ({
  button: {
    height: '36px',
    marginRight: theme.spacing(1),
  },
})

interface Props {
  attachmentCount: number
  closeWinDisabled: boolean
  closeWinErrors?: string[]
  detailPage?: boolean
  locationId: string
  opportunityId: number
  oppStatus: OppStatus
  salesFunnelId: number
  assigneeId: string | null
  branchId: string
  branch: string
  salesTeamId: number
  canUpdate: boolean
  canDelete: boolean
  soldServiceStatus: SOLD_SERVICE_STATUS
  salesFunnelStageId: number
  showStatusSelect?: boolean
  opportunity?: IOpportunity
  closedDate?: Date | null
}

export function OpportunityActions(props: Props) {
  const {
    detailPage,
    opportunityId,
    salesFunnelId,
    assigneeId,
    branchId,
    branch,
    salesTeamId,
    canUpdate,
    canDelete,
    oppStatus,
    locationId,
    soldServiceStatus,
    showStatusSelect,
    opportunity,
    closedDate,
  } = props

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [, closedWonStageId, closedLostStageId, hasOnlySytemFields] =
    useSelectOppStages(salesFunnelId)
  const { isAdmin } = useSelector(selectCurrentUser)
  // ========== S T A T E ==========
  const [reopenPopupOpen, setReopenPopupOpen] = useState<boolean>(false)
  const [backToNotes, setBackToNotes] = useState<boolean>(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const [noteId, setNoteId] = useState<number>(-1)
  const [editNoteDrawerOpen, setEditNoteDrawerOpen] = useState<boolean>(false)

  const [deleteDialogOpen, showDeleteDialog, hideDeleteDialog] =
    useDialogState()
  const [attachmentsDrawerOpen, showAttachmentsDrawer, hideAttachmentsDrawer] =
    useDialogState()
  const [notesDrawerOpen, showNotesDrawer, hideNotesDrawer] = useDialogState()
  const [viewNotesModalOpen, showViewNoteModal, hideViewNoteModal] =
    useDialogState()
  const [reassignFormOpen, showReassignForm, hideReassignForm] =
    useDialogState()

  const [salesTeamLoadingStatus, setSalesTeamLoadingStatus] = useState<
    'pending' | 'fail' | 'success'
  >('pending')

  const locationsStatus = useLoadEntityLocationsOnMount(
    opportunityId,
    ENTITY_TYPES.Opportunity,
    viewNotesModalOpen,
  )
  const { loading, onChangeStatus } = useUpdateOppStatus(
    opportunityId,
    closedWonStageId,
    closedLostStageId,
  )

  const onClickCloseWin = useCallback(
    () => onChangeStatus(closedWonStageId),
    [closedWonStageId, onChangeStatus],
  )

  const handleDeleteOpportunity = useCallback(async () => {
    setDeleteLoading(true)
    const result = await dispatch(deleteOpportunity(opportunityId))

    if (deleteOpportunity.fulfilled.match(result)) {
      setDeleteLoading(false)
      toast.success('Opportunity deleted.')

      if (detailPage) {
        navigate('/opportunities')
        dispatch(clearOpportunityDetail())
      }
    } else {
      setDeleteLoading(false)
      toast.error('Failed to delete opportunity.')
    }
  }, [detailPage, dispatch, navigate, opportunityId])

  const handleReOpenClose = useCallback(() => setReopenPopupOpen(false), [])

  const onSalesTeamSave = useCallback(
    async (teamId, ownerId) => {
      const result = await dispatch(
        updateReassignOpportunity({
          id: opportunityId,
          salesTeamId: teamId,
          assigneeId: ownerId,
          removeServices: false,
        }),
      )
      const updateDetail = await dispatch(getOpportunityDetail(opportunityId))
      if (
        updateReassignOpportunity.fulfilled.match(result) &&
        getOpportunityDetail.fulfilled.match(updateDetail)
      ) {
        toast.success('Opportunity Sales Team updated')
        hideReassignForm()
        // Display the optional toast message if any Draft agreements were updated
        if (result.payload.draftAgreementsUpdated) {
          toast.success('Information has been updated on any draft agreements.')
        }
        dispatch(getOpportunities())
      } else if (getOpportunityDetail.rejected.match(updateDetail)) {
        const { payload } = updateDetail
        if (!!payload) {
          if (payload.StatusCode === 403) {
            dispatch(getOpportunities())
            if (detailPage) navigate('/opportunities')
          }
          if (
            payload.StatusCode === 500 &&
            payload.Message?.toLowerCase() === 'permission denied'
          ) {
            navigate('/access-denied')
          }
        }
      } else {
        toast.error(`Unable to update Opportunity Sales Team.`)
      }
    },
    [opportunityId, dispatch, hideReassignForm, detailPage, navigate],
  )

  useEffect(() => {
    if (reopenPopupOpen) {
      const execute = async () => {
        try {
          setSalesTeamLoadingStatus('pending')
          dispatch(updateLoadingState('visible'))
          await dispatch(getSalesTeam({ id: salesTeamId, includeUsers: true }))
          const result = await dispatch(getSalesTeamUsers(salesTeamId))
          if (getSalesTeamUsers.fulfilled.match(result)) {
            setSalesTeamLoadingStatus('success')
          } else {
            setSalesTeamLoadingStatus('fail')
            toast.error(`Error getting sales team users`)
          }
        } catch (error) {
          console.log('Error fetching sales team users:', error)
          setSalesTeamLoadingStatus('fail')
          toast.error(`Error getting sales team users`)
        } finally {
          dispatch(updateLoadingState('hidden'))
        }
      }

      execute()
    }
  }, [dispatch, salesTeamId, reopenPopupOpen])

  const opportunityOpen = oppStatus === OPPORTUNITY_STATUSES.Open

  const { reopenDisabled, tooltipText: reopenTooltip } = useReopenStatus(
    canUpdate,
    oppStatus,
    soldServiceStatus,
    hasOnlySytemFields,
  )

  const reOpenPopupVariant: ReOpenPopupVariant = useMemo(
    () => soldServiceStatusToReOpenPopupVariant(soldServiceStatus),
    [soldServiceStatus],
  )

  const onNoteAdd = useCallback(() => {
    setBackToNotes(true)
    showNotesDrawer()
  }, [showNotesDrawer])

  const onShowNoteAdd = useCallback(() => {
    setBackToNotes(false)
    showNotesDrawer()
  }, [showNotesDrawer])

  const onGoBack = useCallback(() => {
    hideNotesDrawer()
    if (backToNotes) {
      showViewNoteModal()
    }
  }, [backToNotes, hideNotesDrawer, showViewNoteModal])

  const onGoBackFromEdit = useCallback(() => {
    setEditNoteDrawerOpen(false)
    showViewNoteModal()
  }, [setEditNoteDrawerOpen, showViewNoteModal])

  const onEditNoteClose = useCallback(() => {
    setEditNoteDrawerOpen(false)
  }, [setEditNoteDrawerOpen])

  const onSetBackNotes = useCallback(
    (value: boolean, id: number) => {
      if (value) {
        hideViewNoteModal()
        setEditNoteDrawerOpen(true)
      } else {
        showViewNoteModal()
        setEditNoteDrawerOpen(false)
      }
      setNoteId(id)
    },
    [setNoteId, setEditNoteDrawerOpen, hideViewNoteModal, showViewNoteModal],
  )
  const styles = getStyles(useTheme())
  return (
    <>
      <Stack direction="row" spacing={1.2}>
        {detailPage && (
          <>
            <NotesButton
              onOpenNotesDrawer={onShowNoteAdd}
              canUpdate={canUpdate}
            />

            {!!showStatusSelect && (
              <Box>
                <OpportunityStatusSelect
                  {...props}
                  externalSvcLocId={locationId}
                  disabled={!canUpdate}
                />
              </Box>
            )}
          </>
        )}

        <ButtonWithTooltip
          sx={styles.button}
          color="primary"
          disabled={loading || !canUpdate || !opportunityOpen}
          disableElevation
          onClick={onClickCloseWin}
          showTooltip={!canUpdate}
          tooltipTitle={canUpdate ? undefined : permissionDenied}
          variant="contained"
          data-test-id={`opportunity-closeWinBtn-${opportunityId}`}
        >
          Close / Win
        </ButtonWithTooltip>

        <MoreButton
          dataTestId="opportunityMoreBtn"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          options={[
            {
              label: 'Add/View Note',
              hidden: detailPage,
              onClick: showViewNoteModal,
              disabled: false,
              tooltipText: undefined,
              dataTestId: 'opportunity-add-note',
            },
            {
              label: 'Delete',
              onClick: showDeleteDialog,
              disabled: !canDelete,
              tooltipText: canDelete ? undefined : permissionDenied,
              dataTestId: 'opportunity-DeleteOption',
            },
            {
              label: 'Reassign',
              onClick: showReassignForm,
              disabled: !canUpdate,
              tooltipText: canUpdate ? undefined : permissionDenied,
              dataTestId: 'opportunity-reassignSalesTeam',
            },
            {
              label: 'Re-Open',
              onClick: () => setReopenPopupOpen(true),
              disabled: reopenDisabled,
              tooltipText: reopenTooltip,
              dataTestId: 'opportunity-reopen',
            },
            {
              label: 'Attachments',
              onClick: showAttachmentsDrawer,
              dataTestId: 'opportunity-attachments',
            },
          ]}
        />
      </Stack>
      <AddNoteDrawer
        entityId={opportunityId}
        entityType={ENTITY_TYPES.Opportunity}
        onClose={hideNotesDrawer}
        open={notesDrawerOpen}
        externalLocationId={locationId}
        backToNotes={backToNotes}
        onGoBack={onGoBack}
      />
      {opportunity && (
        <ViewNotesModal
          entityId={opportunityId}
          entityType={ENTITY_TYPES.Opportunity}
          onClose={hideViewNoteModal}
          open={viewNotesModalOpen}
          externalLocationId={locationId}
          canUpdate={canUpdate}
          detail={opportunity}
          onNoteAdd={onNoteAdd}
          backToNotes={true}
          setBackToNotes={onSetBackNotes}
          locationsStatus={locationsStatus}
        />
      )}
      <EditNoteDrawer
        entityId={opportunityId}
        entityType={ENTITY_TYPES.Opportunity}
        noteId={noteId}
        onClose={onEditNoteClose}
        onUpdateSuccess={onEditNoteClose}
        open={editNoteDrawerOpen}
        externalLinkId={locationId}
        backToNotes={true}
        onGoBack={onGoBackFromEdit}
      />
      {/* ========== A T T A C H M E N T S ========== */}

      <AttachmentsDrawer
        entityId={opportunityId}
        entityType={ENTITY_TYPES.Opportunity}
        onClose={hideAttachmentsDrawer}
        open={attachmentsDrawerOpen}
        canUpdate={canUpdate}
      />
      {/* ========== D E L E T E   C O N F I R M   P O P U P ========== */}
      <AlertDialogSlide
        cancelClick={hideDeleteDialog}
        message="Are you sure you want to delete this Opportunity?"
        okButtonText="Delete"
        okClick={handleDeleteOpportunity}
        open={deleteDialogOpen}
        title="Delete Opportunity"
        okButtonDisabled={deleteLoading}
      />
      {/* ========== R E A S S I G N  S A L E S  T E A M  O W N E R ========== */}
      <SideDrawer
        title="Reassign"
        open={reassignFormOpen}
        onClose={hideReassignForm}
      >
        <ReAssignSalesTeamForm
          onClose={hideReassignForm}
          salesTeam={{
            salesTeamId: salesTeamId,
            assigneeId:
              assigneeId === '00000000-0000-0000-0000-000000000000'
                ? null
                : assigneeId,
            branchId: branchId,
            branch: branch,
          }}
          onUpdate={onSalesTeamSave}
          entityType={ENTITY_TYPES.Opportunity}
          entityId={opportunityId}
          showInactiveUsers={isAdmin && Boolean(closedDate)}
        />
      </SideDrawer>
      {/* ========== R E - O P E N   P O P U P ========== */}
      {reopenPopupOpen && salesTeamLoadingStatus === 'success' && (
        <ReOpenOppPopupNew
          onClose={handleReOpenClose}
          open={reopenPopupOpen}
          opportunityId={opportunityId}
          updateOppDetail={detailPage}
          variant={reOpenPopupVariant}
          salesFunnelId={salesFunnelId}
          salesTeamId={salesTeamId.toString()}
          assigneeId={
            assigneeId === '00000000-0000-0000-0000-000000000000'
              ? null
              : assigneeId
          }
        />
      )}
    </>
  )
}
